import React, { useState, useCallback, useRef, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { getRegion } from "@util/domain";
import Card from "@components/cards/Card/Card";
import TextInput from "@components/inputs/TextInput/TextInput";
import Button from "@components/buttons/Button/Button";
import styles from "@styles/CommonAuthPageLayout.module.css";
import { isValidEmail } from "@util/validators";
import { resetPassword } from "@services/authService";
import ReCAPTCHA, { ReCAPTCHAInterface } from "@components/misc/ReCAPTCHA/ReCAPTCHA";

const INVALID_EMAIL_MESSAGE = "Invalid email. Please try again.";

const ForgotPasswordPage = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");
    const [resetError, setResetError] = useState("");
    const [loading, setLoading] = useState(false);

    const recaptchaRef = useRef<ReCAPTCHAInterface>(null);
    const [captchaError, setCaptchaError] = useState<string | null>(null);

    // Validation function for email
    const validateEmail = useCallback((val: string) => {
        return isValidEmail(val);
    }, []);

    // Handlers for email with validation
    const onEmailChange = useCallback((val: string) => {
        setEmail(val);
        setEmailError(validateEmail(val));
    }, [validateEmail]);

    const onEmailBlur = useCallback(() => {
        setEmailError(validateEmail(email));
    }, [email, validateEmail]);

    // Handle form submission with specific error messages
    const handleSubmit = async () => {
        let isValid = true;

        // Validate Email
        const emailValidationError = validateEmail(email);
        if (emailValidationError) {
            setEmailError(emailValidationError);
            isValid = false;
        }

        if (isValid) {
            setResetError("");
            setCaptchaError("");
            setLoading(true); // Show loading

            const captchaToken = await recaptchaRef.current?.execute();
            if (!captchaToken) {
                setCaptchaError("reCAPTCHA verification failed.");
                return;
            }

            try {
                // Simulate the API call for password reset
                await resetPassword(email, captchaToken);

                // Navigate to a confirmation page after success
                navigate("/auth/reset-request-confirmation");
            } catch (error) {
                setResetError(INVALID_EMAIL_MESSAGE);
            } finally {
                setLoading(false); // Hide loading
            }
        } else {
            setResetError(INVALID_EMAIL_MESSAGE);
        }
    };

    return (
        <>
            <Helmet>
                <title>Reset Password | Memorial Hub | Forever In Our Hearts</title>
                <meta
                    name="description"
                    content={`Reset your password to regain access to your online memorials and tributes hub. Quickly restore access and continue ${getRegion() === "global" ? "honoring" : "honouring"} your loved one’s legacy.`}
                />
            </Helmet>
            <div>
                <Card className={styles.cardContainer}>
                    <TextInput
                        label="Email"
                        id="email"
                        value={email}
                        onChange={onEmailChange}
                        onBlur={onEmailBlur}
                        placeholder="Enter email..."
                        errorMessage={emailError}
                        disabled={loading} // Disable while loading
                    />
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={loading} // Disable button during loading
                    >
                        {loading ? "Resetting..." : "Reset"}
                    </Button>
                    {resetError && (<p className={styles.errorMessage}>{resetError}</p>)}
                    {captchaError && <div className={styles.errorMessage}>{captchaError}</div>}
                </Card>
                <ReCAPTCHA ref={recaptchaRef} />
            </div>
        </>
    );
};

export default ForgotPasswordPage;
